// Scale Bridge dashboard — the navigation map.
//
// Pulled out of App.tsx so that the sidebar and the header read from ONE table.
// They were two: a NAV_SECTIONS array and a separate TITLES lookup keyed by the
// same paths, which is a pair that silently drifts — add a screen, forget the
// second table, and the header falls back to "Overview" on a page that is not
// Overview.
//
// Each entry also carries the one-line description the header shows under the
// title. It is there to answer "what am I looking at" for somebody who has just
// been handed the machine, without spending a heading row on every screen.

import {
    ApiOutlined,
    HomeOutlined,
    SettingOutlined,
    TableOutlined,
} from '@ant-design/icons';
import type { ReactNode } from 'react';

export interface NavItem {
    key: string;
    path: string;
    label: string;
    /** Sits under the title in the app header. One line, sentence case. */
    description: string;
    icon: ReactNode;
}

export interface NavSection {
    key: string;
    title: string;
    items: NavItem[];
}

export const NAV_SECTIONS: NavSection[] = [
    {
        key: 'operations',
        title: 'Operations',
        items: [
            {
                key: 'overview',
                path: '/',
                label: 'Overview',
                description: "Today's weighings, the hourly total, and which bridges are reporting",
                icon: <HomeOutlined />,
            },
            {
                key: 'readings',
                path: '/readings',
                label: 'Weighings',
                description: 'Every captured label, newest first. Select a row to see the full record',
                icon: <TableOutlined />,
            },
        ],
    },
    {
        key: 'management',
        title: 'Management',
        items: [
            {
                key: 'devices',
                path: '/devices',
                label: 'Devices',
                description: 'The bridges registered to this account, and the codes waiting to be claimed',
                icon: <ApiOutlined />,
            },
            {
                key: 'settings',
                path: '/settings',
                label: 'Settings',
                description: 'What this computer is connected to, and how it stays up to date',
                icon: <SettingOutlined />,
            },
        ],
    },
];

export const NAV_ITEMS: NavItem[] = NAV_SECTIONS.flatMap((s) => s.items);

/** The nav entry for a pathname, or the Overview entry — which is also the route
 *  an unknown path redirects to, so the header never disagrees with the screen. */
export function navItemFor(pathname: string): NavItem {
    return NAV_ITEMS.find((i) => i.path === pathname) ?? NAV_ITEMS[0];
}

/**
 * The app's scroll container.
 *
 * The shell keeps the sidebar and header still by making the content pane the
 * only thing that scrolls, so a Table asked to stick its header has to be told
 * to watch THAT element rather than the window — which never scrolls here, and
 * would leave the header pinned to a viewport nothing moves in.
 */
export const SCROLL_CONTAINER_ID = 'sb-scroll';

export function getScrollContainer(): HTMLElement | Window {
    return document.getElementById(SCROLL_CONTAINER_ID) ?? window;
}
